import { useCallback, useEffect, useRef, useState } from "react";
import { Globe, Sparkles, Database, HardDrive, Radio } from "lucide-react";

export const WINDOWS = ["15m", "1h", "6h", "24h", "7d"];
export const WIN_MIN = { "15m": 15, "1h": 60, "6h": 360, "24h": 1440, "7d": 10080 };
export const WIN_STEP = { "15m": 1, "1h": 1, "6h": 5, "24h": 15, "7d": 120 };
export const KINDS = {
  http: { label: "HTTP", sub: "Request API", icon: Globe, warn: 1000, crit: 3000 },
  llm: { label: "LLM / AI", sub: "Panggilan provider AI", icon: Sparkles, warn: 10000, crit: 30000 },
  db: { label: "Database", sub: "MongoDB (≥ 100 ms / gagal)", icon: Database, warn: 500, crit: 2000 },
  storage: { label: "Storage", sub: "Object storage", icon: HardDrive, warn: 1000, crit: 3000 },
  ws: { label: "WebSocket", sub: "Kanal user & percakapan", icon: Radio, warn: null, crit: null },
};
export const refreshMs = (win) => (win === "7d" ? 60000 : 30000);
export const SORTS = { p95_ms: "p95", p99_ms: "p99", p50_ms: "p50", avg_ms: "Rata-rata", max_ms: "Maks", count: "Jumlah", errors: "Error", error_rate: "Error rate" };

export const fmtMs = (v) => (v == null ? "—" : v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 1 : 2)} s` : `${v < 10 ? v.toFixed(1) : Math.round(v)} ms`);
export const fmtPct = (v, d = 1) => (v == null ? "—" : `${Number(v).toFixed(d)}%`);
export const fmtRate = (r) => (r == null ? "—" : `${(r * 100).toFixed(r && r < 0.01 ? 2 : 1)}%`);
export const fmtNum = (v) => (v == null ? "—" : Number(v).toLocaleString("id-ID", { maximumFractionDigits: 1 }));
export const fmtBytes = (b) => {
  if (b == null) return "—";
  const u = ["B", "KB", "MB", "GB", "TB"]; let i = 0, n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i += 1; }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
};
export const fmtUptime = (s) => {
  if (s == null) return "—";
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  return d ? `${d}h ${h}j` : h ? `${h}j ${m}m` : m ? `${m}m ${s % 60}d` : `${s}d`;
};
const WIB = { timeZone: "Asia/Jakarta" };
export const fmtWib = (iso, opts = { dateStyle: "medium", timeStyle: "medium" }) => (iso ? `${new Date(iso).toLocaleString("id-ID", { ...WIB, ...opts })} WIB` : "—");
export const fmtTick = (ms, win) => new Date(ms).toLocaleString("id-ID", { ...WIB, ...(win === "7d" || win === "24h" ? { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" } : { hour: "2-digit", minute: "2-digit" }) });
// datetime-local (WIB) <-> ISO UTC
export const wibInputToIso = (v) => (v ? new Date(`${v}:00+07:00`).toISOString() : "");

// Level per brief: error rate >1% kuning, >5% merah; p95 vs per-kind threshold.
export function level(kind, s) {
  if (!s || !s.count) return "idle";
  const k = KINDS[kind];
  // ws: error rate only (session p95 = connection duration).
  if ((s.error_rate || 0) > 0.05 || (k.crit != null && (s.p95_ms || 0) > k.crit)) return "crit";
  if ((s.error_rate || 0) > 0.01 || (k.warn != null && (s.p95_ms || 0) > k.warn)) return "warn";
  return "ok";
}
export const LEVEL_UI = {
  ok: { label: "Sehat", ring: "border-line", badge: "bg-emerald-50 text-emerald-700", bar: "bg-emerald-500" },
  warn: { label: "Waspada", ring: "border-amber-300", badge: "bg-amber-50 text-amber-700", bar: "bg-amber-500" },
  crit: { label: "Kritis", ring: "border-rose-300", badge: "bg-rose-50 text-rose-700", bar: "bg-rose-500" },
  idle: { label: "Tidak ada data", ring: "border-line", badge: "bg-slate-100 text-slate-500", bar: "bg-slate-300" },
};
export const pctTint = (p, base = "bg-brand") => (p == null ? "bg-slate-300" : p >= 90 ? "bg-rose-500" : p >= 75 ? "bg-amber-500" : base);

// Full bucket timeline for the window; missing buckets → `empty` (0 for latency, null for system).
export function fillTimeline(points, win, step, empty) {
  const stepMs = step * 60000, now = Date.now();
  const start = Math.floor((now - WIN_MIN[win] * 60000) / stepMs) * stepMs;
  const map = new Map(points.map((p) => [Math.floor(new Date(p.t).getTime() / stepMs) * stepMs, p]));
  const out = [];
  for (let t = start; t <= now; t += stepMs) out.push({ ...empty, ...(map.get(t) || {}), ts: t });
  return out;
}

export function useMonPrefs() {
  const [win, setWinS] = useState(() => (WINDOWS.includes(localStorage.getItem("orx_mon_window")) ? localStorage.getItem("orx_mon_window") : "1h"));
  const [auto, setAutoS] = useState(() => localStorage.getItem("orx_mon_auto") !== "0");
  const setWin = (w) => { localStorage.setItem("orx_mon_window", w); setWinS(w); };
  const setAuto = (a) => { localStorage.setItem("orx_mon_auto", a ? "1" : "0"); setAutoS(a); };
  return { win, setWin, auto, setAuto };
}

// Calls fn now and every `ms` while `auto`; pauses when the tab is hidden.
export function useAutoRefresh(fn, auto, deps, ms = 30000) {
  const ref = useRef(fn); ref.current = fn;
  const [at, setAt] = useState(null);
  const run = useCallback(async () => { if (await ref.current()) setAt(new Date()); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { run(); }, deps);
  useEffect(() => {
    if (!auto) return undefined;
    const t = setInterval(() => { if (!document.hidden) run(); }, ms);
    const vis = () => { if (!document.hidden) run(); };
    document.addEventListener("visibilitychange", vis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", vis); };
  }, [auto, run, ms]);
  return { refresh: run, at };
}

export const monErr = (e) => {
  const s = e?.response?.status;
  if (s === 404) return "Endpoint monitoring belum tersedia di server (404).";
  if (s === 403) return "Akun Anda bukan staf platform (403).";
  if (s === 422 || s === 400) return e?.response?.data?.detail ? String(typeof e.response.data.detail === "string" ? e.response.data.detail : "Parameter tidak valid") : "Parameter tidak valid.";
  return "Gagal memuat data monitoring.";
};
